import { createContext, FC, PropsWithChildren, useCallback, useEffect, useMemo, useState } from 'react'

import { useLocation } from 'react-router-dom'

import { MenuItems } from '../constants/MenuItems'

export type SidebarContextProps = {
  sidebarOpened: boolean
  menuOpened?: string
  menuSelected?: string
  handleMenuSelected: (id: string) => void
  handleMenuOpened: (id: string) => void
  toggleSidebarOpened: VoidFunction
}

const SidebarContext = createContext<SidebarContextProps>({} as SidebarContextProps)

SidebarContext.displayName = 'Sidebar'

const SidebarProvider: FC<PropsWithChildren> = ({ children }) => {
  const { pathname } = useLocation()

  const [sidebarOpened, setSidebarOpened] = useState(false)
  const [menuSelected, setMenuSelected] = useState<string>()
  const [menuOpened, setMenuOpened] = useState<string>()

  const toggleSidebarOpened = useCallback(() => {
    setSidebarOpened((current) => !current)
  }, [])

  const handleMenuSelected = useCallback((id: string) => {
    setMenuSelected(id)
  }, [])

  const handleMenuOpened = useCallback((id: string) => {
    setMenuOpened((current) => (current !== id ? id : undefined))
  }, [])

  // Set the menu selected and opened based on the current path
  useEffect(() => {
    const currentMainPath = MenuItems.find((item) => item.path === pathname)

    if (currentMainPath) {
      handleMenuSelected(currentMainPath.id)
    }

    const currentChildPath = MenuItems.map((i) => i.items)
      .flatMap((item) => item)
      .find((item) => item?.path === pathname)

    if (currentChildPath) {
      handleMenuSelected(currentChildPath.id)
    }
  }, [handleMenuSelected, pathname])

  const contextValue = useMemo<SidebarContextProps>(
    () => ({ sidebarOpened, menuOpened, menuSelected, handleMenuOpened, handleMenuSelected, toggleSidebarOpened }),
    [handleMenuOpened, handleMenuSelected, menuOpened, menuSelected, sidebarOpened, toggleSidebarOpened],
  )

  return <SidebarContext.Provider value={contextValue}>{children}</SidebarContext.Provider>
}

export { SidebarContext, SidebarProvider }
